/**
 * FieldCasingStringCard — ONE casing string, with ITS OWN torque window.
 *
 * ─── Why this is a component and not a loop body ────────────────────────────
 *
 * `useTorqueProfile` is a hook: five `useState`s, a `useEffect` with a network
 * path, three `useCallback`s. It cannot be called inside
 * `casingStrings.map(...)` — the array is `[]` on the first render and length N
 * once the fetch resolves, so the hook count changes between renders and React
 * throws "Rendered more hooks than during the previous render."
 *
 * A child component per string is therefore the only legal shape. It also
 * happens to be the shape that makes the original bug unrepresentable: there is
 * no job-level torque variable left for a second card to read by accident.
 *
 * ─── The bug this replaces ──────────────────────────────────────────────────
 *
 * FieldJobDetail made ONE `useTorqueProfile` call keyed off the flat
 * `casing_size_inches` / `thread_type` / `casing_weight_lb_ft` / `casing_grade`
 * columns — string 1 — and rendered one card. TNT Integrated Services Ltd
 * (LIVE) ran two strings on J-4290 and the hand on string 2 was shown string
 * 1's window: 3,660 ft-lb optimum against a required 6,680. Under-torqued by
 * 1.8x on a P-110 coupling.
 *
 * (2,710 has been quoted at this bug twice and is wrong both times — it is
 * 4-1/2" 13.5# L-80, a different grade. FieldCasingStringCard.test.tsx pins the
 * real numbers so no one has to read the spec table by eye again.)
 *
 * The size canonicalization below MUST stay inside this component. Hoisting it
 * to the parent is precisely how the second card ends up keyed off the first
 * string's thread again.
 *
 * ─── Entering torque from the field (api#830) ───────────────────────────────
 *
 * A hand on location often holds the mill sheet before the office does, so the
 * card takes torque entry — but ONLY for a combo with nothing on file: no
 * company value and no API RP 5C1 value. The API enforces the same rule (a
 * field POST on an existing combo is 409 `torque_profile_exists`, carrying the
 * stored row); the card never offers the edit in the first place, and when two
 * hands race it shows the row that won rather than a bare refusal.
 *
 * `allowTorqueEntry` is off by default, so the office job page — which edits
 * torque on the job form — renders this card read-only.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Wrench } from 'lucide-react';
import { useTorqueProfile, isKnownShoulderedConnection } from './useTorqueProfile';
import CasingStringCorrection from './CasingStringCorrection';
import { canonicalTorqueSize, canonicalTubingSize, isTubingThread } from '../../data/casingData';
import { getApiErrorMessage } from '../../api/client';
import { torqueProfileExistsOf, type ExistingTorqueProfileRow } from '../../lib/torque-profile-exists';
import type { JobCasingString } from '../../types/casing-string';
import { fmtQty } from '../../lib/display-labels';

interface FieldCasingStringCardProps {
  string: JobCasingString;
  /**
   * Heading suffix. `null` on a single-string job, which renders the plain
   * "Casing Specs" heading the screen has always shown. Derived from
   * `string_number`, never from an array index — a job whose string 1 was
   * deleted must still say "String 2".
   */
  headingSuffix: string | null;
  /**
   * The job, the connectivity flag and the refetch hook for the CORRECTION
   * affordance. All optional: without `jobId` the card is read-only, which is
   * how every existing render of it behaves. The affordance also needs the
   * child row's `id` — a string synthesized from the legacy flat columns has no
   * row for the API to correct.
   */
  jobId?: string;
  isOnline?: boolean;
  onCorrected?: () => void;
  /**
   * Offer torque entry when NOTHING is on file for this string's combo. Never
   * an edit of an existing value — see the file header. Needs a connection:
   * offline, the device cannot know what the office has saved.
   */
  allowTorqueEntry?: boolean;
  /** Replaces the "nothing on file" line — the office page points at the job form. */
  emptyTorqueHint?: string;
  /** Rendered under the spec grid (the office page's formation · depth line). */
  note?: ReactNode;
}

/** ft-lb for display. An absent value is an em dash, never a blank or a zero. */
const ft = fmtQty;

interface ReadoutValues {
  min: number | string | null;
  optimum: number | string | null;
  max: number | string | null;
  low: number | string | null;
  high: number | string | null;
  /** Whether the shoulder row renders — the combo's flag, not the values. */
  shouldered: boolean;
}

function TorqueReadout({ v, badge }: { v: ReadoutValues; badge: ReactNode }) {
  return (
    <div className="border-t border-fieldiq-border pt-3 mt-2">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-semibold text-fieldiq-accent uppercase tracking-wider">Makeup Torque</p>
        {badge}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-fieldiq-dark rounded-lg p-2.5 text-center">
          <p className="text-fieldiq-muted text-[10px]">Min</p>
          <p className="text-white font-bold">{ft(v.min)}</p>
          <p className="text-fieldiq-muted text-[10px]">ft-lb</p>
        </div>
        <div className="bg-fieldiq-dark rounded-lg p-2.5 text-center border border-fieldiq-accent/30">
          <p className="text-fieldiq-accent text-[10px]">Optimum</p>
          <p className="text-fieldiq-accent font-bold">{ft(v.optimum)}</p>
          <p className="text-fieldiq-muted text-[10px]">ft-lb</p>
        </div>
        <div className="bg-fieldiq-dark rounded-lg p-2.5 text-center">
          <p className="text-fieldiq-muted text-[10px]">Max</p>
          <p className="text-white font-bold">{ft(v.max)}</p>
          <p className="text-fieldiq-muted text-[10px]">ft-lb</p>
        </div>
      </div>
      {v.shouldered && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="bg-fieldiq-dark rounded-lg p-2.5 text-center">
            <p className="text-fieldiq-muted text-[10px]">Low Shoulder</p>
            <p className="text-white font-bold text-sm">{ft(v.low)} <span className="text-[10px] font-normal text-fieldiq-muted">ft-lb</span></p>
          </div>
          <div className="bg-fieldiq-dark rounded-lg p-2.5 text-center">
            <p className="text-fieldiq-muted text-[10px]">High Shoulder</p>
            <p className="text-white font-bold text-sm">{ft(v.high)} <span className="text-[10px] font-normal text-fieldiq-muted">ft-lb</span></p>
          </div>
        </div>
      )}
    </div>
  );
}

const entryInput =
  'w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none';

/** '' is absent; anything else must be a non-negative whole number of ft-lb. */
const parseFtLb = (raw: string): number | null | 'invalid' => {
  const t = raw.trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 'invalid';
};

export default function FieldCasingStringCard({
  string: s,
  headingSuffix,
  jobId,
  isOnline = true,
  onCorrected,
  allowTorqueEntry = false,
  emptyTorqueHint,
  note,
}: FieldCasingStringCardProps) {
  // Per string. Not per job.
  const threadRaw = s.thread_type || '';
  const sizeRaw = s.size_inches || '';
  const torqueSizeLabel = isTubingThread(threadRaw) ? canonicalTubingSize(sizeRaw) : canonicalTorqueSize(sizeRaw);
  // `weight_lb_ft` is varchar in production and holds '13.5' and '23.00' on two
  // strings of the same job. parseFloat handles both; a non-numeric free-text
  // value yields NaN, which the `Number.isFinite` guard turns into null rather
  // than a weight of 0 that would silently match the wrong spec row.
  const parsedWeight = s.weight_lb_ft != null ? parseFloat(s.weight_lb_ft) : NaN;

  const {
    torque: torqueProfile,
    status: torqueStatus,
    saveTorqueProfile,
    refresh,
  } = useTorqueProfile({
    size: torqueSizeLabel,
    weightPpf: Number.isFinite(parsedWeight) ? parsedWeight : null,
    grade: (s.grade || '') as any,
    connectionType: (threadRaw || '') as any,
  });

  const hasTorque = !!torqueProfile && torqueStatus !== 'idle' && torqueProfile.torqueMin != null;

  // ─── Entry, for a combo with nothing on file ─────────────────────────────
  const [entry, setEntry] = useState({ min: '', optimum: '', max: '', low: '', high: '' });
  const [shouldered, setShouldered] = useState(() => isKnownShoulderedConnection(threadRaw));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The row that beat this hand to it, straight off the 409. Shown in place of
  // the entry, so the hand runs to what is on file and not to what they typed.
  const [onFile, setOnFile] = useState<ExistingTorqueProfileRow | null>(null);

  // A different pipe (a correction changed the spec) starts a fresh entry, with
  // the checkbox defaulted for ITS connection.
  useEffect(() => {
    setEntry({ min: '', optimum: '', max: '', low: '', high: '' });
    setShouldered(isKnownShoulderedConnection(threadRaw));
    setOnFile(null);
    setSaveError(null);
  }, [torqueSizeLabel, s.weight_lb_ft, s.grade, threadRaw]);

  // A miss from the RESOLVER, not a failed lookup and not the offline library
  // (which cannot see a company value): only then is "nothing on file" known.
  const isMiss =
    torqueStatus === 'resolved' &&
    (!torqueProfile || torqueProfile.source === null) &&
    !torqueProfile?.fromOfflineFallback;
  const canEnter = allowTorqueEntry && isOnline && isMiss && !onFile;

  const parsed = {
    min: parseFtLb(entry.min),
    optimum: parseFtLb(entry.optimum),
    max: parseFtLb(entry.max),
    low: shouldered ? parseFtLb(entry.low) : null,
    high: shouldered ? parseFtLb(entry.high) : null,
  };
  const anyInvalid = Object.values(parsed).some((v) => v === 'invalid');
  const canSave = !saving && !anyInvalid && parsed.min !== null && parsed.optimum !== null && parsed.max !== null;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveTorqueProfile({
        torqueMin: parsed.min as number,
        torqueOptimum: parsed.optimum as number,
        torqueMax: parsed.max as number,
        torqueLowShoulder: parsed.low as number | null,
        torqueHighShoulder: parsed.high as number | null,
        shouldered,
      });
      // The hook re-resolved; the saved value now renders read-only above.
    } catch (err) {
      const exists = torqueProfileExistsOf(err);
      if (exists) {
        if (exists.profile) setOnFile(exists.profile);
        else {
          setSaveError('Torque for this pipe was saved by someone else a moment ago. Loading it…');
          try { await refresh(); setSaveError(null); } catch { /* the message stands */ }
        }
      } else {
        setSaveError(getApiErrorMessage(err, 'Torque was not saved. Check your connection and try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
      <h3 className="font-semibold text-white flex items-center gap-2">
        <Wrench size={16} className="text-fieldiq-accent" /> Casing Specs{headingSuffix ? ` — ${headingSuffix}` : ''}
      </h3>
      {/*
        All four cells ALWAYS render, and an absent value renders as an em dash.
        The old card dropped the whole cell instead, which was survivable when
        there was one card: a narrow card meant a thin job record. With N cards
        side by side it stops being survivable — a string missing its grade
        would silently borrow the shape of its neighbour, and "this cell is
        blank" and "this screen does not show grade" become indistinguishable.
        A fully populated string looks exactly as it did before.
      */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-fieldiq-muted text-xs block">Casing Size</span>
          <span className="text-white font-medium">
            {s.size_inches ? `${s.size_inches}"${s.size_mm ? ` / ${s.size_mm}mm` : ''}` : '—'}
          </span>
        </div>
        <div>
          <span className="text-fieldiq-muted text-xs block">Weight</span>
          <span className="text-white font-medium">{s.weight_lb_ft ? `${s.weight_lb_ft} lb/ft` : '—'}</span>
        </div>
        <div>
          <span className="text-fieldiq-muted text-xs block">Connection</span>
          <span className="text-white font-medium">{s.thread_type || '—'}</span>
        </div>
        <div>
          <span className="text-fieldiq-muted text-xs block">Grade</span>
          <span className="text-white font-medium">{s.grade || '—'}</span>
        </div>
      </div>
      {note}

      {/*
        Who wrote this spec, and the way to correct it — directly under the four
        values the torque window below is resolved from, so a hand reading the
        number can see where its inputs came from.
      */}
      {jobId && s.id && (
        <CasingStringCorrection
          jobId={jobId}
          string={s}
          isOnline={isOnline}
          onChanged={onCorrected ?? (() => {})}
        />
      )}

      {/* Torque Values */}
      {hasTorque && (
        <TorqueReadout
          v={{
            min: torqueProfile!.torqueMin,
            optimum: torqueProfile!.torqueOptimum,
            max: torqueProfile!.torqueMax,
            low: torqueProfile!.torqueLowShoulder,
            high: torqueProfile!.torqueHighShoulder,
            // The combo's flag. Where the resolver gave none (the offline
            // library, an older API), whether shoulder values arrived — which is
            // what this card keyed on before the flag existed.
            shouldered: torqueProfile!.shouldered
              ?? (torqueProfile!.torqueLowShoulder != null || torqueProfile!.torqueHighShoulder != null),
          }}
          badge={
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              torqueProfile!.source === 'api_spec' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' : 'bg-green-500/15 text-green-400 border border-green-500/30'
            }`}>{torqueProfile!.sourceLabel}</span>
          }
        />
      )}

      {/* Someone else saved this combo first (409). Their row, verbatim. */}
      {onFile && !hasTorque && (
        <>
          <div role="alert" className="border border-fieldiq-border rounded-lg p-3 text-xs text-fieldiq-text bg-fieldiq-dark">
            Torque for this pipe is already on file — someone saved it while you were entering yours, and
            yours was not saved. Run to the values below. Only the office can change them.
          </div>
          <TorqueReadout
            v={{
              min: onFile.torque_min,
              optimum: onFile.torque_optimum,
              max: onFile.torque_max,
              low: onFile.torque_low_shoulder,
              high: onFile.torque_high_shoulder,
              shouldered: onFile.shouldered === true,
            }}
            badge={
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-500/15 text-green-400 border border-green-500/30">
                Company value — on file
              </span>
            }
          />
        </>
      )}

      {/*
        A card with no torque block, next to a card that has one, is the exact
        misread this whole change exists to stop: the numbers on the other card
        look like they cover both strings. Say plainly that this string has no
        window rather than showing nothing at all. `torqueStatus` is named so
        the hand can tell "still loading" from "there is nothing to load".
      */}
      {!hasTorque && !onFile && torqueStatus !== 'idle' && (
        <div className="border-t border-fieldiq-border pt-3 mt-2 space-y-3">
          <p className="text-xs text-fieldiq-muted">
            {torqueStatus === 'loading'
              ? 'Loading makeup torque…'
              : torqueStatus === 'error'
                ? 'Makeup torque unavailable — could not reach the server.'
                : emptyTorqueHint ?? 'No makeup torque on file for this string.'}
          </p>

          {canEnter && (
            <div className="space-y-3" data-testid="field-torque-entry">
              <p className="text-xs text-fieldiq-muted">
                Enter the manufacturer's figures. They are saved for your company and recalled on every job
                with this exact pipe. Once saved, only the office can change them.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {([['min', 'Min'], ['optimum', 'Optimum'], ['max', 'Max']] as const).map(([k, label]) => (
                  <label key={k} className="text-[10px] text-fieldiq-muted">
                    {label} (ft-lb)
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      aria-label={`${label} torque, ft-lb`}
                      className={entryInput}
                      value={entry[k]}
                      onChange={(e) => { setEntry({ ...entry, [k]: e.target.value }); setSaveError(null); }}
                    />
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-white">
                <input
                  type="checkbox"
                  checked={shouldered}
                  onChange={(e) => { setShouldered(e.target.checked); setSaveError(null); }}
                />
                Shouldered connection
              </label>
              {shouldered && (
                <div className="grid grid-cols-2 gap-2">
                  {([['low', 'Low shoulder'], ['high', 'High shoulder']] as const).map(([k, label]) => (
                    <label key={k} className="text-[10px] text-fieldiq-muted">
                      {label} (ft-lb)
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        aria-label={`${label} torque, ft-lb`}
                        className={entryInput}
                        value={entry[k]}
                        onChange={(e) => { setEntry({ ...entry, [k]: e.target.value }); setSaveError(null); }}
                      />
                    </label>
                  ))}
                </div>
              )}
              {anyInvalid && (
                <p className="text-xs text-fieldiq-text">Torque must be a whole number of ft-lb, 0 or more.</p>
              )}
              <button
                type="button"
                disabled={!canSave}
                onClick={handleSave}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl text-sm disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save torque'}
              </button>
              {saveError && (
                <p role="alert" className="text-xs text-fieldiq-text">
                  <strong>Not saved.</strong> {saveError}
                </p>
              )}
            </div>
          )}
          {allowTorqueEntry && !isOnline && isMiss && (
            <p className="text-xs text-fieldiq-muted">Entering torque needs a connection.</p>
          )}
        </div>
      )}
    </div>
  );
}
